import ClearIcon from '@mui/icons-material/Clear'
import FlagIcon from '@mui/icons-material/Flag'
import SearchIcon from '@mui/icons-material/Search'
import { IconButton } from '@mui/material'
import Image from 'next/image'
import { Fragment, useEffect, useMemo, useState } from 'react'
import VisibilityEyeIcon from 'src/components/Icons/VisibilityEyeIcon'
import { StyledTab, StyledTabs } from 'src/components/StyledTabs'
import { useFastCasualContext } from '../context/FastCasualContext'
import EmailVerificationComp from './EmailVerificationComp'
import TableComp from './TableComp'

// Common interface for brand data with minimum required fields for filtering
interface BrandData {
  'Brand Name': string
  [key: string]: string | number | boolean | undefined
}

// SVG Placeholder component for missing brand logos
const LogoPlaceholder = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 100 100"
    xmlns="http://www.w3.org/2000/svg"
    className="w-16 h-16 md:w-20 md:h-20">
    <rect width="100" height="100" rx="8" fill="#F9FAFB" />
    <circle cx="50" cy="38" r="18" fill="#E5E7EB" />
    <rect x="30" y="60" width="40" height="6" rx="3" fill="#E5E7EB" />
    <rect x="35" y="70" width="30" height="4" rx="2" fill="#E5E7EB" />
    <path
      d="M50 25 L50 33 M42 38 L58 38"
      stroke="#9CA3AF"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect
      x="1"
      y="1"
      width="98"
      height="98"
      rx="7"
      stroke="#E5E7EB"
      strokeWidth="1"
      fill="none"
    />
  </svg>
)

// Brand Logo component with proper hooks usage
interface BrandLogoProps {
  brandName: string
  verified: boolean
}

const BrandLogo = ({ brandName, verified }: BrandLogoProps) => {
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    // Reset error state when brand name changes
    setImageError(false)
  }, [brandName])

  if (!verified) return null

  return (
    <div className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0 flex items-center justify-center relative">
      {!imageError ? (
        <img
          key={`brand-logo-${brandName}`}
          src={`/api/fast_casual/${encodeURIComponent(brandName)}`}
          alt={brandName}
          loading="lazy"
          style={{
            objectFit: 'contain',
            width: '100%',
            height: '100%',
            maxWidth: '100%'
          }}
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <LogoPlaceholder />
        </div>
      )}
    </div>
  )
}

export default function FastCasualTableWrapper() {
  const [tabValue, setTabValue] = useState('delivery')
  const { verified: temp } = useFastCasualContext()
  const verified = temp
  const [dataLoading, setDataLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [appScoreData, setAppScoreData] = useState<Record<string, any>[]>([])
  const [doordashData, setDoordashData] = useState<Record<string, any>[]>([])
  const [socialMediaData, setSocialMediaData] = useState<Record<string, any>[]>(
    []
  )

  const infoText = useMemo(() => {
    if (tabValue === 'delivery') {
      return 'Uncover how your brand ranks with customers on Delivery Platform evaluated by ratings, reviews, and store-level engagement.'
    } else if (tabValue === 'social_media') {
      return 'Discover your social media footprint—measuring brand visibility through follower counts across major platforms.'
    } else {
      return 'See where your brand stands in the digital age—scored by app ratings, downloads, and customer feedback across app stores.'
    }
  }, [tabValue])

  const tableData = useMemo(() => {
    const result = (() => {
      if (tabValue === 'app_score') return appScoreData
      if (tabValue === 'delivery') return doordashData
      if (tabValue === 'social_media') return socialMediaData
      return []
    })()
    return result as BrandData[]
    // .filter((e) => '100-500' !== e.Bucket && e.Bucket !== '>500')
  }, [tabValue, appScoreData, doordashData, socialMediaData])

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const filteredTableData = useMemo(() => {
    if (!searchQuery.trim()) return tableData

    // Use type assertion to avoid TypeScript complexity with union types
    return (tableData as Array<Record<string, any>>).filter((item) => {
      if (typeof item['Brand Name'] === 'string') {
        return item['Brand Name']
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      }
      return false
    })
  }, [tableData, searchQuery])

  useEffect(() => {
    if (verified) {
      const getTableData = async () => {
        setDataLoading(true)
        const appScoreData = await fetch(
          '/fast_casual_index_json/app_score_performance_cohort.json'
        )
        const doordashData = await fetch(
          '/fast_casual_index_json/three_p_delivery_performance_cohort.json'
        )
        const socialMediaData = await fetch(
          '/fast_casual_index_json/social_media_performance_cohort.json'
        )

        setAppScoreData(await appScoreData.json())
        setDoordashData(await doordashData.json())
        setSocialMediaData(await socialMediaData.json())
        setDataLoading(false)
      }
      getTableData()
    } else {
      // set dummy data to the states
      const arr = Array.from({ length: 5 }).map((_, index) => {
        return {
          Rank: index + 1,
          Ranking: index + 1,
          'Brand Name': `Dummy Brand ${index + 1}`,
          Bucket: `Bucket ${index + 1}`,
          'App Score': `Score ${index + 1}`,
          'Doordash Score Normalise': `Score ${index + 1}`,
          'Social Media Ranked Score': `Score ${index + 1}`
        }
      })
      setAppScoreData(
        Array.from({ length: 5 }).map((_, index) => {
          return {
            'Brand Name': `string ${index}`,
            'Locations Bucket': `string ${index}`,
            'Google Play Store Rating Range': `string ${index}`,
            'Google Play Store #Ratings Range': `string ${index}`,
            'Apple Store Rating Range': `string ${index}`,
            'Apple Store #Ratings Range': `string ${index}`
          }
        })
      )
      setDoordashData(
        Array.from({ length: 5 }).map((_, index) => {
          return {
            'Brand Name': `string ${index}`,
            'Locations Bucket': `string ${index}`,
            'Avg Rating Range': `string ${index}`,
            'Rating per Store Range': `string ${index}`,
            'Reviews per Store Range': `string ${index}`,
            'Avg Price Range': `string ${index}`
          }
        })
      )
      setSocialMediaData(
        Array.from({ length: 5 }).map((_, index) => {
          return {
            'Brand Name': `string ${index}`,
            'Locations Bucket': `string ${index}`,
            'FB followers Range': `string ${index}`,
            'Insta followers range': `string ${index}`,
            'Twitter followers range': `string ${index}`,
            'LinkedIn followers range': `string ${index}`
          }
        })
      )
    }
  }, [verified])

  const renderBrandName = (data: Record<string, any>) => {
    const brandName = data['Brand Name']

    if (!searchQuery.trim()) {
      return (
        <div className="flex flex-row items-center gap-3">
          <BrandLogo brandName={brandName} verified={verified} />
          <div className="min-w-0 flex-1">
            <span
              className="text-sm font-semibold text-[#374151] line-clamp-2 p-4"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                width: '100%',
                maxWidth: '200px',
                minHeight: '2.5rem'
              }}>
              {brandName}
            </span>
          </div>
        </div>
      )
    }

    // Find the match index for highlighting
    const lowerBrandName = brandName.toLowerCase()
    const lowerQuery = searchQuery.toLowerCase()
    const matchIndex = lowerBrandName.indexOf(lowerQuery)

    if (matchIndex === -1) {
      return (
        <div className="flex flex-row items-center gap-3">
          <BrandLogo brandName={brandName} verified={verified} />
          <div className="min-w-0 flex-1">
            <span
              className="text-sm font-semibold text-[#374151] line-clamp-2"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                width: '100%',
                maxWidth: '200px',
                minHeight: '2.5rem'
              }}>
              {brandName}
            </span>
          </div>
        </div>
      )
    }

    const beforeMatch = brandName.substring(0, matchIndex)
    const match = brandName.substring(
      matchIndex,
      matchIndex + searchQuery.length
    )
    const afterMatch = brandName.substring(matchIndex + searchQuery.length)

    return (
      <div className="flex flex-row items-center gap-3">
        <BrandLogo brandName={brandName} verified={verified} />
        <div className="min-w-0 flex-1">
          <span
            className="text-sm font-semibold text-[#374151] line-clamp-2"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              width: '100%',
              maxWidth: '200px',
              minHeight: '2.5rem'
            }}>
            {beforeMatch}
            <span className="bg-yellow-200">{match}</span>
            {afterMatch}
          </span>
        </div>
      </div>
    )
  }

  return (
    <Fragment>
      <div className="relative">
        <div
          className={
            `relative p-3 mb-3 border border-gray-200 rounded-lg ` +
            (verified && !dataLoading
              ? ''
              : 'blur-sm pointer-events-none select-none')
          }>
          <div className="absolute top-0 left-0 right-0 -translate-y-1/2 flex justify-center w-full">
            <StyledTabs
              className="md:flex-row flex-col"
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              sx={{
                '&, & *:not(button *)': {
                  overflowX: 'visible !important'
                }
              }}>
              <StyledTab
                label="3PD PLATFORM PERFORMANCE"
                value="delivery"
                // sx={{
                //   maxHeight: 'none !important',
                //   lineHeight: '2rem',
                //   px: '24px',
                //   fontSize: '0.75rem'
                // }}
              />
              <StyledTab
                label="SOCIAL MEDIA PERFORMANCE"
                value="social_media"
                // sx={{
                //   maxHeight: 'none !important',
                //   lineHeight: '2rem',
                //   px: '24px',
                //   fontSize: '0.75rem'
                // }}
              />
              <StyledTab
                label="APP PERFORMANCE"
                value="app_score"
                className="max-h-none leading-8 px-6 text-xs"
                // sx={{
                //   maxHeight: 'none !important',
                //   lineHeight: '2rem',
                //   px: '24px',
                //   fontSize: '0.75rem'
                // }}
              />
            </StyledTabs>
          </div>

          <p className="mt-11 text-black opacity-50 text-center text-xs">
            {infoText}
          </p>

          <div className="mt-5 mb-4 max-w-md mx-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon
                  className="h-5 w-5 text-gray-400"
                  fontSize="small"
                />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none">
                    <ClearIcon className="h-5 w-5" fontSize="small" />
                  </button>
                </div>
              )}
            </div>
            {filteredTableData.length === 0 && searchQuery && (
              <div className="text-xs text-gray-500 mt-1 text-center">
                No brands found matching "{searchQuery}"
              </div>
            )}
          </div>

          <div className="mt-3 h-full overflow-y-hidden">
            <TableComp
              data={filteredTableData}
              // customColumnClassName={() => 'group'}
              columns={[
                {
                  title: 'Brand Name',
                  field: 'Brand Name',
                  sortable: true,
                  width: '280px',
                  render: renderBrandName
                },
                {
                  title: 'Locations Bucket',
                  field: 'Locations Bucket',
                  width: '150px',
                  sortable: true
                },
                {
                  title: 'Avg. Rating',
                  field: 'Avg Rating Range',
                  width: '120px',
                  sortable: true,
                  hidden: tabValue !== 'delivery'
                },
                {
                  title: 'Rating Per Store',
                  field: 'Rating per Store Range',
                  width: '150px',
                  sortable: true,
                  hidden: tabValue !== 'delivery'
                },
                {
                  title: 'Reviews Per Store',
                  field: 'Reviews per Store Range',
                  width: '150px',
                  sortable: true,
                  hidden: tabValue !== 'delivery'
                },
                {
                  title: 'Avg. Price',
                  field: 'Avg Price Range',
                  width: '120px',
                  sortable: true,
                  hidden: tabValue !== 'delivery'
                },
                {
                  title: 'FB Followers',
                  titleIcon: (
                    <Image
                      src="/img/logos/short_facebook.svg"
                      alt="Facebook"
                      width={24}
                      height={24}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ),
                  field: 'FB followers Range',
                  width: '160px',
                  sortable: true,
                  hidden: tabValue !== 'social_media'
                },
                {
                  title: 'Instagram Followers',
                  titleIcon: (
                    <Image
                      src="/img/logos/short_instagram.svg"
                      alt="instagram"
                      width={24}
                      height={24}
                      style={{ width: '100%', height: '100%' }}
                    />
                  ),
                  field: 'Insta followers range',
                  width: '160px',
                  sortable: true,
                  hidden: tabValue !== 'social_media'
                },
                {
                  title: 'Twitter Followers',
                  titleIcon: (
                    <Image
                      src="/img/logos/short_twitter.svg"
                      alt="Twitter"
                      width={24}
                      height={24}
                      style={{ width: '100%', height: '100%' }}
                    />
                  ),
                  field: 'Twitter followers range',
                  width: '160px',
                  sortable: true,
                  hidden: tabValue !== 'social_media'
                },
                {
                  title: 'LinkedIn Followers',
                  titleIcon: (
                    <Image
                      src="/img/logos/short_linkedin.svg"
                      alt="LinkedIn"
                      width={24}
                      height={24}
                      style={{ width: '100%', height: '100%' }}
                    />
                  ),
                  field: 'LinkedIn followers range',
                  width: '160px',
                  sortable: true,
                  hidden: tabValue !== 'social_media'
                },
                {
                  title: 'Google Play Rating',
                  field: 'Google Play Store Rating Range',
                  width: '150px',
                  sortable: true,
                  hidden: tabValue !== 'app_score'
                },
                {
                  title: 'Google Play Reviews',
                  field: 'Google Play Store #Ratings Range',
                  width: '150px',
                  sortable: true,
                  hidden: tabValue !== 'app_score'
                },
                {
                  title: 'Apple Store Rating',
                  field: 'Apple Store Rating Range',
                  width: '120px',
                  sortable: true,
                  hidden: tabValue !== 'app_score'
                },
                {
                  title: 'Apple Store Reviews',
                  field: 'Apple Store #Ratings Range',
                  width: '120px',
                  sortable: true,
                  hidden: tabValue !== 'app_score'
                },
                {
                  title: 'Score',
                  field: 'Score',
                  hidden: true,
                  sortable: false,
                  render: (data) => {
                    return (
                      <>
                        <div className="flex flex-row items-center gap-2">
                          <span className="text-black text-base">
                            {'App Score' in data
                              ? data['App Score']
                              : 'Doordash Score Normalise' in data
                              ? data['Doordash Score Normalise']
                              : 'Social Media Ranked Score' in data
                              ? data['Social Media Ranked Score']
                              : ''}
                          </span>
                          <IconButton
                            LinkComponent={'a'}
                            href="https://fhmjrhdp.paperform.co/"
                            target="_blank"
                            className="group-hover:visible group-hover:opacity-100 opacity-0 transition-all text-lg invisible p-2 rounded-full"
                            style={{
                              color: '#E75926'
                              // color: '#FFFFFF'
                            }}>
                            <FlagIcon fontSize="inherit" color="inherit" />
                          </IconButton>
                        </div>
                      </>
                    )
                  }
                }
              ]}
            />
          </div>
        </div>
        {!verified && (
          <div className="flex flex-col items-center justify-center absolute top-0 left-0 right-0 bottom-0">
            <VisibilityEyeIcon sx={{ fontSize: '35px', color: '#000' }} />
            <p className="mt-7 text-base text-black text-center">
              Unlock full insights and exclusive data by signing up below
            </p>
            <div className="mt-8">
              <EmailVerificationComp
                buttonLabel="Sign Up To View Full Index"
                successLabel="Request is successfully sent"
                position="center"
              />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  )
}
